import { Box, Container, Flex, retriever } from '@retriever-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import CarouselV2 from 'src/components/carousel-v2'
import { useWindowWidth } from 'src/store/context/window.context'

export interface TestimonialProps {
  text: string
  name: string
  company: string
  city: string
  Icon?: string
}

export const TestimonialCard = ({
  text,
  name,
  company,
  city,
  Icon,
}: TestimonialProps) => {
  const { windowWidth } = useWindowWidth()

  return (
    <Flex
      width={['350px', '570px']}
      height={['340px', '300px']}
      margin="0 auto"
      pX={[20, 40]}
      pY={40}
      borderRadius="16px"
      backgroundColor="primary.400"
      justifyContent="space-between"
      direction="column"
      gap="40px"
    >
      <retriever.p
        fontSize={['14px', '16px']}
        color="black.0"
        fontWeight="bold"
        textAlign="start"
      >{`"${text.toUpperCase()}"`}</retriever.p>
      <Flex justifyContent="space-between">
        <Flex direction="column" alignContent="end">
          <retriever.p fontSize="14px" color="black.0" fontWeight="bold">
            {name}
          </retriever.p>
          {windowWidth > 720 && (
            <retriever.p fontSize="14px" color="black.0">
              {company}
            </retriever.p>
          )}
          <retriever.p fontSize="14px" color="black.0">
            {city}
          </retriever.p>
        </Flex>
        <Box>
          <retriever.img src={Icon} />
        </Box>
      </Flex>
    </Flex>
  )
}

const TestimonialsGallery = () => {
  const { t } = useTranslation('landing-pages/growth')
  const { windowWidth } = useWindowWidth()

  return (
    <CarouselV2
      isInfinite
      customStylesDesktop={{
        minWidth: '690px',
        maxWidth: '690px',
        minHeight: '300px',
        maxHeight: '300px',
      }}
      customStylesMobile={{
        minWidth: '350px',
        maxWidth: '350px',
        minHeight: '375px',
        maxHeight: '375px',
      }}
      autoPlay={true}
      withIndicators={true}
      withArrows={windowWidth > 720 ? true : false}
      CustomLeftArrow={undefined}
      CustomRightArrow={undefined}
      CustomIndicator={undefined}
    >
      {testimonials(t).map(({ text, name, company, city, Icon }) => (
        <TestimonialCard
          key={name}
          text={text}
          name={name}
          company={company}
          city={city}
          Icon={Icon}
        />
      ))}
    </CarouselV2>
  )
}

const Testimonials = () => {
  const { t } = useTranslation('landing-pages/growth')
  return (
    <Flex
      direction="column"
      backgroundColor="black.500"
      pBottom="40px"
      mBottom={[60, 120]}
    >
      <Container alignItems="center" justifyContent="center">
        <Flex
          alignItems="center"
          justifyContent="center"
          direction={['column-reverse', 'row']}
          gap={['0', '15px']}
          mTop={80}
          mBottom={[40, 60]}
        >
          <Box>
            <retriever.p
              fontSize="24px"
              fontWeight="bold"
              color="#FFFFFF"
              textAlign={['center', 'initial']}
            >
              {t('landing-pages/growth:testimonials.TITLE')}
            </retriever.p>
          </Box>
          <Box>
            <retriever.img
              src={`${process.env.REACT_APP_CDN_IMAGE}/https://housi-images.s3.amazonaws.com/site/landing-pages/growth/housi-ballon.png`}
            />
          </Box>
        </Flex>
        <TestimonialsGallery />
      </Container>
    </Flex>
  )
}

export const testimonials = (t: any) => [
  {
    text: t('landing-pages/growth:testimonials.M_MACHADO.TEXT'),
    name: t('landing-pages/growth:testimonials.M_MACHADO.NAME'),
    company: t('landing-pages/growth:testimonials.M_MACHADO.COMPANY'),
    city: t('landing-pages/growth:testimonials.M_MACHADO.CITY'),
  },
  {
    text: t('landing-pages/growth:testimonials.F_FIGUEIREDO.TEXT'),
    name: t('landing-pages/growth:testimonials.F_FIGUEIREDO.NAME'),
    company: t('landing-pages/growth:testimonials.F_FIGUEIREDO.COMPANY'),
    city: t('landing-pages/growth:testimonials.F_FIGUEIREDO.CITY'),
    Icon: `${process.env.REACT_APP_CDN_IMAGE}/https://housi-images.s3.amazonaws.com/site/landing-pages/growth/logo-yes.png`,
  },
  {
    text: t('landing-pages/growth:testimonials.J_FIUZA.TEXT'),
    name: t('landing-pages/growth:testimonials.J_FIUZA.NAME'),
    company: t('landing-pages/growth:testimonials.J_FIUZA.COMPANY'),
    city: t('landing-pages/growth:testimonials.J_FIUZA.CITY'),
    Icon: `${process.env.REACT_APP_CDN_IMAGE}/https://housi-images.s3.amazonaws.com/site/landing-pages/growth/logo-diagonal.png`,
  },
  {
    text: t('landing-pages/growth:testimonials.F_NOBREGA.TEXT'),
    name: t('landing-pages/growth:testimonials.F_NOBREGA.NAME'),
    company: t('landing-pages/growth:testimonials.F_NOBREGA.COMPANY'),
    city: t('landing-pages/growth:testimonials.F_NOBREGA.CITY'),
    Icon: `${process.env.REACT_APP_CDN_IMAGE}/https://housi-images.s3.amazonaws.com/site/landing-pages/growth/logo-alta.png`,
  },
  {
    text: t('landing-pages/growth:testimonials.G_CHARTUNI.TEXT'),
    name: t('landing-pages/growth:testimonials.G_CHARTUNI.NAME'),
    company: t('landing-pages/growth:testimonials.G_CHARTUNI.COMPANY'),
    city: t('landing-pages/growth:testimonials.G_CHARTUNI.CITY'),
  },
  {
    text: t('landing-pages/growth:testimonials.A_DUMBROSCK.TEXT'),
    name: t('landing-pages/growth:testimonials.A_DUMBROSCK.NAME'),
    company: t('landing-pages/growth:testimonials.A_DUMBROSCK.COMPANY'),
    city: t('landing-pages/growth:testimonials.A_DUMBROSCK.CITY'),
    Icon: `${process.env.REACT_APP_CDN_IMAGE}/https://housi-images.s3.amazonaws.com/site/landing-pages/growth/logo-flamboyant.png`,
  },
  {
    text: t('landing-pages/growth:testimonials.D_ARANOVICH.TEXT'),
    name: t('landing-pages/growth:testimonials.D_ARANOVICH.NAME'),
    company: t('landing-pages/growth:testimonials.D_ARANOVICH.COMPANY'),
    city: t('landing-pages/growth:testimonials.D_ARANOVICH.CITY'),
  },
]

export default Testimonials
